<script lang="ts" setup>
  import type { Region } from '~/core/types/regions'
  import { BngModal } from '~/layouts/components'

  type Props = {
    region: Region & { thumb: string, description: string }
  }

  const props = defineProps<Props>()

  const { t } = useI18n()
  const readMoreContent = ref(false)
</script>
<template>
  <div class="c-region-resume">
    <div class="illustration">
      <img
        :src="props.region.thumb"
        :alt="region.slug"
      >
    </div>
    <div class="detail">
      <div class="content">
        <p class="title">
          <span>Découvrez les golfs de la région {{ region.name }}</span>
        </p>
        <p class="golfs">
          <span>{{ props.region.golfs_count }} {{ t("common.golf", props.region.golfs_count) }}</span>
        </p>
        <p class="description">
          <span>{{ props.region.description }}</span>
        </p>
        <a
          class="read-more"
          @click="readMoreContent = true"
        >Lire plus</a>
      </div>
    </div>
    <Teleport
      v-if="readMoreContent"
      to="#modal"
    >
      <BngModal
        foreground
        :hide-backdrop="false"
        closable
        @close="readMoreContent = false"
      >
        <div class="read-more-modal">
          <h3>
            <span>Découvrez les golfs de la région {{ region.name }}</span>
          </h3>
          <div class="read-more-modal-content">
            <p>{{ region.description }}</p>
          </div>
        </div>
      </BngModal>
    </Teleport>
  </div>
</template>
<style scoped>

.read-more-modal {
  display: flex;
  overflow: hidden;
  flex: 1 1 auto;
  flex-direction: column;
  padding: 1.5em;
  font-size: 1rem;

  h3 {
    span {
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 1em;
      font-weight:700;
      opacity: 0.8;
    }

    padding-bottom: 1em;
  }

  .read-more-modal-content {
    height: 100%;
    overflow-y: auto;
    white-space: pre-line;

    p {
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 1em;
      font-weight:normal;
      line-height: initial;
      opacity: 0.8;
    }
  }
}

.c-region-resume {
  display: flex;
  width: 100%;
  max-width: 1600px;
  flex-direction: column;
  margin: 3em auto;

  .illustration {
    display: flex;
    overflow: hidden;
    width: 100%;
    height: 30vh;
    align-items: center;
    justify-content: center;

    img {
      display: flex;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .detail {
    .content {
      display: flex;
      flex-direction: column;
      padding: 2.75em 3em;
      background: white;
      box-shadow: 0 0 25px rgb(0 0 0 / 26%);

      .title {
        span {
          color: var(--color-secondary);
          font-family: Bariol, sans-serif;
          font-size: 1.75em;
          font-weight: 700;
        }
      }

      .golfs {
        margin: 0.25em 0 0.5em;

        span {
          color: var(--color-primary);
          font-family: Open-Sans, sans-serif;
          font-size: 1.125em;
          font-weight: 700;
        }
      }

      .description {
        display: -webkit-box;
        overflow: hidden;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 4;
        white-space: pre-line;

        span {
          color: var(--color-secondary);
          font-family: Open-Sans, sans-serif;
          font-size: 1em;
          font-weight: 500;
          opacity: 0.7;
        }
      }

      .read-more {
        align-self: flex-end;
        margin-top: 0.5rem;
        color: var(--color-primary);
        cursor: pointer;
        font-family: Open-Sans, sans-serif;
        font-size: 1em;
        font-weight: 500;
        text-decoration: underline;
      }
    }
  }

  @media screen and (width >= 1024px) {
    display: grid;
    grid-template-columns: repeat(12, 1fr);

    .illustration {
      height: auto;
      aspect-ratio: 3 / 2;
      grid-column: 1 / 8;
      grid-row: 1;
    }

    .detail {
      display: flex;
      grid-column: 6 / 13;
      grid-row: 1;

      .content {
        align-self: flex-end;
      }
    }
  }

  @media screen and (width >= 1280px) {
    .illustration {
      grid-column: 1 / 7;
    }

    .detail {
      grid-column: 5 / 13;

      .content {
        align-self: center;

        .title {
          span {
            font-size: 2em;
          }
        }

        .golfs {
          span {
            font-size: 1.25em;
          }
        }

        .description {
          display: flex;

          span {
            font-size: 1em;
          }
        }

        .read-more {
          display: none;
        }
      }
    }
  }

  @media screen and (width >= 1600px) {
    .detail {
      grid-column: 6 / 13;

      .content {
        .title {
          span {
            font-size: 2.25em;
          }
        }

        .golfs {
          span {
            font-size: 1.375em;
          }
        }

        .description {
          span {
            font-size: 1.125em;
          }
        }
      }
    }
  }
}
</style>
